import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ElementRef,
  ViewChild,
  AfterViewInit,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { Button } from '../button/button';
import { CommonModule } from '@angular/common';
import { ThemeService } from '../services/theme';
import { Option } from '../option/option';
import { focusableElements, trapTab } from '../services/focus-trap';

@Component({
  selector: 'app-info-modal',
  templateUrl: './info-modal.html',
  styleUrls: ['./info-modal.scss'],
  standalone: true,
  imports: [Button, CommonModule, Option, RouterLink],
})
export class InfoModal implements OnInit, AfterViewInit, OnDestroy {
  @Input() title = 'Title';
  @Input() actionLabel = 'Confirm';
  @Input() showClose = true; // show/hide top-right close button
  @Input() closeOnBackdrop = true; // clicking backdrop closes modal
  @Input() initialOpen = false;
  @Input() maxWidth!: string;
  // Optional - form
  @Input() options!: string[];
  @Input() selectedOptions!: string[];

  @Output() action = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() setOptions = new EventEmitter<string[]>();

  @ViewChild('dialog') dialogEl!: ElementRef<HTMLElement>;

  toggleOption(option: string) {
    const updated = [...this.selectedOptions];
    const index = updated.indexOf(option);

    if (index === -1) {
      updated.push(option);
    } else {
      updated.splice(index, 1);
    }
    this.setOptions.emit(updated);
  }

  private themeService = inject(ThemeService);

  get isLight() {
    return this.themeService.isLight();
  }

  get closeIcon() {
    return this.isLight ? '/close-light.svg' : '/close.svg';
  }

  isOpen = false;
  private previouslyFocused?: Element | null;

  ngOnInit() {
    this.isOpen = !!this.initialOpen;
  }

  ngAfterViewInit() {
    if (this.isOpen) this.open();
  }

  ngOnDestroy() {
    this.unlockScroll();
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lockScroll();
    this.saveAndFocus();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.unlockScroll();
    this.restoreFocus();
    this.closed.emit();
  }

  confirm() {
    this.action.emit();
  }

  onBackdropClick(evt: MouseEvent) {
    if (!this.closeOnBackdrop) return;
    // Only close if click happened directly on backdrop, not inside dialog
    if ((evt.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.close();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      trapTab(this.dialogEl.nativeElement, event);
    }
  }

  // ----- focus handling -----
  private saveAndFocus() {
    this.previouslyFocused = document.activeElement;
    setTimeout(() => {
      const dialog = this.dialogEl?.nativeElement;
      if (!dialog) return;
      (focusableElements(dialog)[0] ?? dialog).focus();
    });
  }

  private restoreFocus() {
    try {
      (this.previouslyFocused as HTMLElement | null)?.focus();
    } catch {
      // ignore
    }
    this.previouslyFocused = null;
  }

  // ----- page scroll lock -----
  private lockScroll() {
    document.body.style.overflow = 'hidden';
  }
  private unlockScroll() {
    document.body.style.overflow = '';
  }
}
